import {
  Text,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import csvjson from 'csvjson';
import React, { useState } from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import { ExportBufferButton } from './ExportBufferButton';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { FiPlay } from 'react-icons/fi';

const getProgressColor = (progressPercent: number, error: string | null) => {
  if (!error) {
    return 'green.400';
  }

  if (progressPercent === 0) {
    return 'gray.400';
  }

  return 'red.400';
};

const createBuffer = (data: any, downloadType: DownloadType) => {
  switch (downloadType) {
    case 'csv':
      finleyAssert(Array.isArray(data), 'expected array');
      return csvjson.toCSV(data, {
        headers: 'key',
        wrap: true,
      });
    case 'json': {
      const json = JSON.stringify(data);
      const encoder = new TextEncoder();
      return encoder.encode(json);
    }
    default:
      return finleyAssertUnreachable(downloadType);
  }
};

const renderErrorOrProgress = (
  error: string | null,
  progressText: string | null,
) => {
  if (error) {
    return (
      <Alert status="error" variant="subtle">
        <AlertIcon />
        {error}
      </Alert>
    );
  }
  if (progressText) {
    return <Text ml={2}>{progressText}</Text>;
  }

  return null;
};

export type DownloadType = 'csv' | 'json';
// encapsulates a button that starts a long-running process
// and allows to download the result when it's done
type Props = {
  onStart: () => Promise<any | null>;
  filename: string;
  buttonText: string;
  progressPercent: number;
  progressText: string;
};
export const ProgressWithDownloadButton = ({
  onStart,
  filename,
  progressPercent,
  progressText,
  buttonText,
}: Props) => {
  const [isRunning, setIsRunning] = useState(false);
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const downloadType = filename.split('.').pop();
  finleyAssert(
    downloadType === 'csv' || downloadType === 'json',
    'invalid file extension',
  );

  const onPlay = async () => {
    setIsRunning(true);
    setErrorText(null);
    try {
      const result = await onStart();
      if (result === null) {
        setBuffer(null);
      } else {
        setBuffer(createBuffer(result, downloadType));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(`${buttonText} Error:`, e);
      if (e instanceof Error) {
        setErrorText(e.message);
      } else {
        setErrorText(`Unknown error:  ${String(e)}`);
      }
      setIsRunning(false);
      return;
    }
    setIsRunning(false);
  };

  const progressColor = getProgressColor(progressPercent, errorText);

  // we render "play", "download" or progress percent
  // depending on the state of the progress and error.
  const renderCircularProgressLabelContent = () => {
    if (!isRunning) {
      if (progressPercent === 0) {
        return (
          <IconButton
            onClick={onPlay}
            isDisabled={false}
            icon={<FiPlay />}
            ariaLabel="Run"
            label="Run"
          />
        );
      }
      if (progressPercent === 100) {
        return <ExportBufferButton filename={filename} buffer={buffer} />;
      }
    }
    return `${Math.round(progressPercent)} %`;
  };
  return (
    <Flex alignItems="center">
      <CircularProgress
        ml={2}
        size="80px"
        value={progressPercent}
        color={progressColor}
        thickness="8px"
      >
        <CircularProgressLabel>
          {renderCircularProgressLabelContent()}
        </CircularProgressLabel>
      </CircularProgress>
      <Text ml={2}>{buttonText}</Text>
      {renderErrorOrProgress(errorText, progressText)}
    </Flex>
  );
};
